import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArticleForm from '../components/ArticleForm';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import '../styles/Admin.css';
import { checkMigrationStatus, migrateArticlesToFirestore } from '../utils/migrateArticles';

const Admin = () => {
  const [signups, setSignups] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('signups');
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading, logout } = useAuth();

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading) {
      if (!user || !isAdmin) {
        navigate('/admin-login');
      }
    }
  }, [user, isAdmin, authLoading, navigate]);

  const handleSaveArticle = async (articleData) => {
    try {
      // Validate required fields
      if (!articleData.title || !articleData.excerpt || !articleData.content) {
        throw new Error('Missing required fields: title, excerpt, or content');
      }

      // Clean and validate data before sending to Firestore
      const cleanArticleData = {
        title: String(articleData.title || '').trim(),
        excerpt: String(articleData.excerpt || '').trim(),
        content: String(articleData.content || '').trim(),
        slug: String(articleData.slug || '').trim(),
        date: String(articleData.date || new Date().toISOString().split('T')[0]),
        readTime: String(articleData.readTime || '5 min read'),
        author: String(articleData.author || 'LogiCar Team'),
        featured: Boolean(articleData.featured),
        image: String(articleData.image || ''),
        category: String(articleData.category || 'Maintenance')
      };

      // Additional safety: ensure no nested objects or arrays
      const sanitizedData = {};
      Object.keys(cleanArticleData).forEach(key => {
        let value = cleanArticleData[key];
        
        // Handle null/undefined
        if (value === null || value === undefined) {
          value = '';
        }
        
        // Handle arrays
        if (Array.isArray(value)) {
          value = value.join(', ');
        }
        
        // Handle objects
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value);
        }
        
        // Ensure it's a primitive type
        if (typeof value === 'string') {
          sanitizedData[key] = value;
        } else if (typeof value === 'boolean') {
          sanitizedData[key] = value;
        } else if (typeof value === 'number') {
          sanitizedData[key] = value;
        } else {
          // Convert everything else to string
          sanitizedData[key] = String(value);
        }
      });

      // Debug: Log the original and cleaned data
      console.log('Original articleData:', articleData);
      console.log('Cleaned articleData:', cleanArticleData);
      console.log('Sanitized data:', sanitizedData);
      console.log('Data types:', Object.keys(sanitizedData).map(key => `${key}: ${typeof sanitizedData[key]}`));
      
      // Deep inspection of each field
      Object.keys(sanitizedData).forEach(key => {
        const value = sanitizedData[key];
        console.log(`Field ${key}:`, {
          value: value,
          type: typeof value,
          isArray: Array.isArray(value),
          isObject: typeof value === 'object' && value !== null,
          isNull: value === null,
          isUndefined: value === undefined,
          length: value?.length,
          stringified: JSON.stringify(value)
        });
        
        if (value === null || value === undefined) {
          console.warn(`Warning: ${key} is ${value}`);
        }
        if (typeof value === 'object' && value !== null) {
          console.warn(`Warning: ${key} is an object:`, value);
        }
        if (Array.isArray(value)) {
          console.warn(`Warning: ${key} is an array:`, value);
        }
      });

      // Validate cleaned data
      if (!cleanArticleData.title || !cleanArticleData.excerpt || !cleanArticleData.content) {
        throw new Error('Required fields cannot be empty after cleaning');
      }

      // Final validation - check if data is Firestore-compatible
      try {
        JSON.stringify(sanitizedData);
        console.log('Data is JSON serializable');
      } catch (jsonError) {
        console.error('Data is not JSON serializable:', jsonError);
        throw new Error('Data contains non-serializable values');
      }

      if (editingArticle) {
        // Update existing article in Firestore (or create if it doesn't exist)
        console.log('Updating article:', editingArticle.id);
        const articleRef = doc(db, 'articles', String(editingArticle.id));
        
        try {
          await updateDoc(articleRef, sanitizedData);
          console.log('Article updated successfully in Firestore');
          
          // Update local state
          const updatedArticles = articles.map(article => 
            article.id === editingArticle.id ? { ...article, ...sanitizedData } : article
          );
          setArticles(updatedArticles);
        } catch (firestoreError) {
          console.error('Firestore update error:', firestoreError);
          
          // If article doesn't exist, create it instead
          if (firestoreError.code === 'not-found') {
            console.log('Article not found in Firestore, creating new article...');
            try {
              const docRef = await addDoc(collection(db, 'articles'), sanitizedData);
              console.log('Article created successfully in Firestore with new ID:', docRef.id);
              
              // Remove old article and add new one to local state
              const updatedArticles = articles
                .filter(article => article.id !== editingArticle.id)
                .concat({ id: docRef.id, ...sanitizedData });
              setArticles(updatedArticles);
              
              alert('Article was saved with a new ID. Local article has been removed.');
            } catch (createError) {
              console.error('Error creating article:', createError);
              throw new Error(`Failed to create article: ${createError.message}`);
            }
          } else if (firestoreError.code === 'permission-denied') {
            console.error('Permission denied details:', {
              userEmail: user?.email,
              userUID: user?.uid,
              isAdmin: isAdmin
            });
            throw new Error('Permission denied. Please ensure you are logged in as an admin user and the Firestore rules have been deployed.');
          } else if (firestoreError.code === 'unauthenticated') {
            throw new Error('Authentication required. Please log in to continue.');
          } else {
            throw new Error(`Failed to update article: ${firestoreError.message}`);
          }
        }
      } else {
        // Add new article to Firestore
        try {
          const docRef = await addDoc(collection(db, 'articles'), sanitizedData);
          const newArticle = { id: docRef.id, ...sanitizedData };
          
          // Update local state
          setArticles([...articles, newArticle]);
          console.log('Article created successfully in Firestore');
        } catch (firestoreError) {
          console.error('Firestore create error:', firestoreError);
          console.error('Error details:', {
            code: firestoreError.code,
            message: firestoreError.message,
            stack: firestoreError.stack
          });
          
          // Handle specific authentication errors
          if (firestoreError.code === 'permission-denied') {
            console.error('Permission denied details:', {
              userEmail: user?.email,
              userUID: user?.uid,
              isAdmin: isAdmin
            });
            throw new Error('Permission denied. Please ensure you are logged in as an admin user and the Firestore rules have been deployed.');
          } else if (firestoreError.code === 'unauthenticated') {
            throw new Error('Authentication required. Please log in to continue.');
          } else {
            throw new Error(`Failed to create article: ${firestoreError.message}`);
          }
        }
      }
      
      setShowArticleForm(false);
      setEditingArticle(null);
    } catch (error) {
      console.error('Error saving article:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Failed to save article: ${errorMessage}. Please try again.`);
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setShowArticleForm(true);
  };

  const handleDeleteArticle = async (articleId) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        console.log('Deleting article with ID:', articleId);
        
        const articleRef = doc(db, 'articles', String(articleId));
        await deleteDoc(articleRef);
        console.log('Successfully deleted article from Firestore');
        
        // Update local state
        const updatedArticles = articles.filter(article => article.id !== articleId);
        setArticles(updatedArticles);
        
        // Refresh articles from Firestore to ensure consistency
        await fetchArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
        
        // Handle specific errors
        if (error.code === 'permission-denied') {
          alert('Permission denied. Please ensure you are logged in as an admin user.');
        } else if (error.code === 'not-found') {
          alert('Article not found in database. It may have already been deleted.');
        } else {
          alert(`Failed to delete article: ${error.message}. Please try again.`);
        }
      }
    }
  };

  const handleMigration = async () => {
    try {
      setMigrating(true);
      const result = await migrateArticlesToFirestore();
      
      if (result.success) {
        alert(`Migration successful! ${result.message}`);
        // Refresh articles after migration
        const articlesRef = collection(db, 'articles');
        const q = query(articlesRef, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        const articlesData = querySnapshot.docs.map(doc => ({
          id: String(doc.id), // Ensure ID is string
          ...doc.data()
        }));
        setArticles(articlesData);
      } else {
        alert(`Migration failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Migration error:', error);
      alert('Migration failed. Please try again.');
    } finally {
      setMigrating(false);
    }
  };

  const checkMigration = async () => {
    const status = await checkMigrationStatus();
    setMigrationStatus(status);
  };

  const fetchArticles = async () => {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const articlesData = querySnapshot.docs.map(doc => ({
        id: String(doc.id), // Ensure ID is string
        ...doc.data()
      }));
      setArticles(articlesData);
      console.log('Fetched articles from Firestore:', articlesData.length);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    }
  };

  const refreshArticles = async () => {
    // Refresh articles from Firestore
    console.log('Refreshing articles from Firestore...');
    await fetchArticles();
  };

  useEffect(() => {
    if (!user || !isAdmin) return;

    const fetchSignups = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, "signups"), orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);
        
        const signupData = [];
        querySnapshot.forEach((doc) => {
          signupData.push({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp.toDate().toLocaleString()
          });
        });
        
        setSignups(signupData);
        setError(null);
      } catch (err) {
        console.error("Error fetching signups: ", err);
        setError("Failed to load signups. Please try again later.");
      } finally {
        setLoading(false);
      }
    };


    fetchSignups();
    fetchArticles();
    checkMigration();
  }, [user, isAdmin]);

  if (authLoading) {
    return (
      <div className="admin-login">
        <div className="admin-login-container">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null; // Will redirect to login
  }

  return (
    <div className="admin-page">
      <SEO 
        title="Admin Dashboard - LogiCar"
        description="Admin dashboard for managing LogiCar website content and signups"
        canonical="https://www.logicarapp.com/admin"
      />
      
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-title">
            <h1>Admin Dashboard</h1>
            <p>Manage your website content and view signups</p>
          </div>
          <div className="admin-actions">
            <button onClick={logout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>

        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'signups' ? 'active' : ''}`}
            onClick={() => setActiveTab('signups')}
          >
            <i className="fas fa-users"></i>
            Signups ({signups.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
            onClick={() => setActiveTab('articles')}
          >
            <i className="fas fa-newspaper"></i>
            Articles ({articles.length})
          </button>
        </div>

        {activeTab === 'signups' && (
          <div className="signup-section">
            {loading ? (
              <div className="loading">Loading...</div>
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
              <>
                <div className="signup-stats">
                  <div className="stat-card">
                    <h3>{signups.length}</h3>
                    <p>Total Sign-ups</p>
                  </div>
                  <div className="stat-card">
                    <h3>{signups.filter(s => new Date(s.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}</h3>
                    <p>This Week</p>
                  </div>
                </div>
                
                <div className="signup-table-container">
                  <table className="signup-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signups.length > 0 ? (
                        signups.map((signup) => (
                          <tr key={signup.id}>
                            <td>{signup.name || 'N/A'}</td>
                            <td>{signup.email}</td>
                            <td>{signup.timestamp}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="no-data">No sign-ups yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="export-section">
                  <button 
                    onClick={() => {
                      const csvContent = "data:text/csv;charset=utf-8," 
                        + "Name,Email,Date\n"
                        + signups.map(s => `${s.name || 'N/A'},${s.email},${s.timestamp}`).join("\n");
                      
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement("a");
                      link.setAttribute("href", encodedUri);
                      link.setAttribute("download", "logicar_signups.csv");
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="export-button"
                  >
                    <i className="fas fa-download"></i>
                    Export to CSV
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'articles' && (
          <div className="articles-section">
            <div className="articles-header">
              <h2>Blog Articles</h2>
              <div className="articles-actions">
                {migrationStatus?.needsMigration && (
                  <button 
                    onClick={handleMigration}
                    disabled={migrating}
                    className="migrate-btn"
                  >
                    <i className="fas fa-cloud-upload-alt"></i>
                    {migrating ? 'Migrating...' : 'Migrate to Cloud'}
                  </button>
                )}
                <button 
                  onClick={refreshArticles}
                  className="refresh-btn"
                  title="Refresh articles from Firestore"
                >
                  <i className="fas fa-sync-alt"></i>
                  Refresh
                </button>
                <button 
                  onClick={() => setShowArticleForm(true)}
                  className="add-article-btn"
                >
                  <i className="fas fa-plus"></i>
                  Add Article
                </button>
              </div>
            </div>
            
            {migrationStatus?.needsMigration && (
              <div className="migration-notice">
                <i className="fas fa-info-circle"></i>
                <span>Articles are currently stored locally. Click "Migrate to Cloud" to sync them across all devices.</span>
              </div>
            )}

            {articles.length === 0 ? (
              <div className="no-articles">
                <i className="fas fa-newspaper"></i>
                <h3>No articles yet</h3>
                <p>Create your first article to get started</p>
                <button 
                  onClick={() => setShowArticleForm(true)}
                  className="create-first-btn"
                >
                  Create First Article
                </button>
              </div>
            ) : (
              <div className="articles-grid">
                {articles.map(article => (
                  <div key={article.id} className="article-card">
                    {article.image && (
                      <div className="article-image">
                        <img src={article.image} alt={article.title} />
                      </div>
                    )}
                    <div className="article-header">
                      <h3>{article.title}</h3>
                      <div className="article-meta">
                        <span className="date">{new Date(article.date).toLocaleDateString()}</span>
                        {article.featured && <span className="featured-badge">Featured</span>}
                      </div>
                    </div>
                    <div className="article-content">
                      <p>{article.excerpt}</p>
                    </div>
                    <div className="article-actions">
                      <button 
                        onClick={() => handleEditArticle(article)}
                        className="edit-btn"
                      >
                        <i className="fas fa-edit"></i>
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteArticle(article.id)}
                        className="delete-btn"
                      >
                        <i className="fas fa-trash"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showArticleForm && (
          <ArticleForm
            article={editingArticle}
            onSave={handleSaveArticle}
            onCancel={() => {
              setShowArticleForm(false);
              setEditingArticle(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Admin; 